/**
 * Freight operations: labels, form validators and parsers shared by the customer portal's operations screens
 * (apps/web /admin) and the Admin Console's Freight modules (apps/console). Framework-free, so both apps and their
 * tests use one copy. Every validator mirrors the API's zod schema; the API re-checks everything.
 * Money is integer cents throughout and is formatted with `format(usd(...))` from ./money.
 */
import { format, usd } from "./money.js";

const money = (cents: number): string => format(usd(cents));
/** "$25,000" for whole-dollar limits; cents shown only when present. */
export const wholeDollars = (cents: number): string => money(cents).replace(/\.00$/, "");
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;

/* ---------------- Dispatch ---------------- */

export const FOLLOW_UP_REASON_KEYS = ["exception", "missed_pickup", "late", "no_pro", "stale_tracking"] as const;
export type FollowUpReason = (typeof FOLLOW_UP_REASON_KEYS)[number];
export const FOLLOW_UP_REASON_TEXT: Record<FollowUpReason, string> = {
  exception: "Exception",
  missed_pickup: "Missed pickup",
  late: "Past ETA",
  no_pro: "No PRO",
  stale_tracking: "Tracking stale",
};
export const FOLLOW_UP_NOTE_MAX = 500;
/** Snooze choices in hours (0 = keep it in the queue). The API caps a snooze at 72 hours. */
export const SNOOZE_CHOICES = [0, 1, 4, 24, 72] as const;

/** Mirrors the API zod: trimmed 1-500 chars, no control characters. Returns an error message or null. */
export function validateFollowUpNote(note: string): string | null {
  const v = note.trim();
  if (!v) return "Add a note so the team knows what's happening.";
  if (v.length > FOLLOW_UP_NOTE_MAX) return `Keep the note under ${FOLLOW_UP_NOTE_MAX} characters.`;
  if (CONTROL.test(v)) return "Remove special control characters.";
  return null;
}

/** YYYY-MM-DD shifted by whole days, calendar-safe across month/DST boundaries. */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const isIsoDate = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s) && shiftDate(s, 0) === s;

/* ---------------- Claims ---------------- */

export const CLAIM_TYPE_KEYS = ["damage", "loss", "shortage", "concealed_damage", "delay"] as const;
export type ClaimTypeKey = (typeof CLAIM_TYPE_KEYS)[number];
export const CLAIM_STATUS_KEYS = ["draft", "filed", "under_review", "approved", "denied", "paid", "withdrawn"] as const;
export type ClaimStatusKey = (typeof CLAIM_STATUS_KEYS)[number];
export const CLAIM_TYPE_TEXT: Record<ClaimTypeKey, string> = {
  damage: "Damage",
  loss: "Loss",
  shortage: "Shortage",
  concealed_damage: "Concealed damage",
  delay: "Delay",
};
export const CLAIM_STATUS_TEXT: Record<ClaimStatusKey, string> = {
  draft: "Draft",
  filed: "Filed",
  under_review: "Under review",
  approved: "Approved",
  denied: "Denied",
  paid: "Paid",
  withdrawn: "Withdrawn",
};
/** Mirrors the API cap on a resolution note. */
export const CLAIM_NOTE_MAX = 2000;
const CLAIM_MAX_CENTS = 1_000_000_000;

/** Parses a paid amount; unlike a claim amount, $0 is allowed (a claim approved but settled at nothing). */
export function parsePaidAmount(text: string): number | null {
  const t = text.trim().replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const cents = Math.round(Number(t) * 100);
  return cents <= CLAIM_MAX_CENTS ? cents : null;
}

export type ClaimDecision = { note?: string; paidAmountCents?: number };

/** Validates a staff decision form; returns the request extras or an error message for the person. */
export function validateClaimDecision(
  status: ClaimStatusKey,
  note: string,
  paidText: string,
): { ok: true; extra: ClaimDecision } | { ok: false; field: "note" | "paid"; message: string } {
  const n = note.trim();
  if (n.length > CLAIM_NOTE_MAX) return { ok: false, field: "note", message: `Keep the note under ${CLAIM_NOTE_MAX} characters.` };
  if (status === "denied" && !n) return { ok: false, field: "note", message: "Add a note telling the customer why the claim was denied." };
  const extra: ClaimDecision = n ? { note: n } : {};
  if (status === "paid") {
    const cents = parsePaidAmount(paidText);
    if (cents === null) return { ok: false, field: "paid", message: "Enter the amount paid, like 1250.00." };
    extra.paidAmountCents = cents;
  }
  return { ok: true, extra };
}

/* ---------------- Credit ---------------- */

export const TERMS_OPTIONS = [7, 15, 30, 45, 60] as const;
export const MIN_LIMIT_CENTS = 10_000; // $100
export const MAX_LIMIT_CENTS = 100_000_000; // $1,000,000
export const CREDIT_NOTE_MAX = 1000;

/** Dollar input -> cents within the credit limit bounds, or an error message. */
export function parseLimit(raw: string): { cents?: number; error?: string } {
  const s = raw.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return { error: "Enter a dollar amount, for example 25000." };
  const cents = Math.round(Number(s) * 100);
  if (cents < MIN_LIMIT_CENTS) return { error: `Minimum is ${wholeDollars(MIN_LIMIT_CENTS)}.` };
  if (cents > MAX_LIMIT_CENTS) return { error: `Maximum is ${wholeDollars(MAX_LIMIT_CENTS)}.` };
  return { cents };
}

export type CreditDecisionIn =
  | { decision: "approve"; termsDays: number; limitCents: number; note: string | null }
  | { decision: "reject"; note: string };
export type CreditDecisionForm = { decision: "approve" | "reject"; terms: string; limit: string; note: string };

/** Mirrors the API's CreditDecisionIn: approve needs listed terms + limit in bounds; reject needs a note. */
export function checkCreditDecision(f: CreditDecisionForm): { body?: CreditDecisionIn; errors: Partial<Record<"terms" | "limit" | "note", string>> } {
  const errors: Partial<Record<"terms" | "limit" | "note", string>> = {};
  const note = f.note.trim();
  if (note.length > CREDIT_NOTE_MAX) errors.note = `Keep the note under ${CREDIT_NOTE_MAX} characters.`;
  if (f.decision === "reject") {
    if (!note) errors.note = "Give the customer a reason.";
    return Object.keys(errors).length ? { errors } : { errors, body: { decision: "reject", note } };
  }
  const terms = Number(f.terms);
  if (!(TERMS_OPTIONS as readonly number[]).includes(terms)) errors.terms = "Choose payment terms.";
  const limit = parseLimit(f.limit);
  if (limit.error) errors.limit = limit.error;
  if (Object.keys(errors).length) return { errors };
  return { errors, body: { decision: "approve", termsDays: terms, limitCents: limit.cents!, note: note || null } };
}

/* ---------------- Billing reconciliation ---------------- */

export const VARIANCE_FLAG_KEYS = ["unbilled", "match", "over", "under"] as const;
export type VarianceFlagKey = (typeof VARIANCE_FLAG_KEYS)[number];
export const DISPUTE_STATUS_KEYS = ["none", "open", "won", "lost"] as const;
export type DisputeStatusKey = (typeof DISPUTE_STATUS_KEYS)[number];
export const REBILL_REASON_KEYS = ["reweigh", "reclass", "accessorial", "other"] as const;
export type RebillReasonKey = (typeof REBILL_REASON_KEYS)[number];
export const REBILL_MODE_KEYS = ["cost", "markup"] as const;
export type RebillModeKey = (typeof REBILL_MODE_KEYS)[number];
export const REBILL_NOTE_MAX = 500;
export const DISPUTE_NOTE_MAX = 1000;
/** Mirrors the API's billedCents bound ($1,000,000). */
export const MAX_BILL_CENTS = 100_000_000;

const REBILL_REASON_TEXT: Record<RebillReasonKey, string> = { reweigh: "Reweigh", reclass: "Reclass", accessorial: "Accessorial", other: "Other" };
export const rebillReasonText = (r: RebillReasonKey) => REBILL_REASON_TEXT[r];
const REBILL_MODE_TEXT: Record<RebillModeKey, string> = { cost: "At cost (pass the overage through)", markup: "With markup (scale by the shipment's price/cost)" };
export const rebillModeText = (m: RebillModeKey) => REBILL_MODE_TEXT[m];

/** Unsigned dollars ("812", "$1,050.5") -> integer cents without float math. Zero is allowed. */
export function parseDollars(text: string): { cents: number } | { error: string } {
  const t = text.trim().replace(/[$,\s]/g, "");
  const m = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return { error: t === "" ? "Enter the billed amount" : "Use dollars and cents, like 812.40" };
  const cents = Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
  if (cents > MAX_BILL_CENTS) return { error: "Amount can't exceed $1,000,000" };
  return { cents };
}

const realDate = (d: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(d) && !Number.isNaN(Date.parse(`${d}T00:00:00Z`)) && new Date(`${d}T00:00:00Z`).toISOString().startsWith(d);

export type BillForm = { invoiceNumber: string; invoiceDate: string; amount: string };
export type BillErrors = Partial<Record<keyof BillForm, string>>;
export type BillInput = { carrierInvoiceNumber: string; invoiceDate: string | null; billedCents: number; invoiceDocumentId?: string | null };

/** Mirrors the API BillIn schema: printable invoice # 1-64, optional real date, cents. */
export function parseBillForm(f: BillForm): { input: BillInput } | { errors: BillErrors } {
  const errors: BillErrors = {};
  const num = f.invoiceNumber.trim();
  if (!num) errors.invoiceNumber = "Enter the carrier invoice number";
  else if (num.length > 64) errors.invoiceNumber = "Keep it to 64 characters";
  else if (!/^[\x20-\x7E]+$/.test(num)) errors.invoiceNumber = "Use plain letters, numbers and punctuation";
  const date = f.invoiceDate.trim();
  if (date && !realDate(date)) errors.invoiceDate = "Use a real date";
  const amt = parseDollars(f.amount);
  if ("error" in amt) errors.amount = amt.error;
  if (Object.keys(errors).length || "error" in amt) return { errors };
  return { input: { carrierInvoiceNumber: num, invoiceDate: date || null, billedCents: amt.cents } };
}

/** Same table as the API's DISPUTE_TRANSITIONS. */
const DISPUTE_MOVES: Record<DisputeStatusKey, readonly DisputeStatusKey[]> = { none: ["open"], open: ["won", "lost", "none"], won: ["open"], lost: ["open"] };
export const disputeMoves = (from: DisputeStatusKey): readonly DisputeStatusKey[] => DISPUTE_MOVES[from];
const MOVE_TEXT: Record<DisputeStatusKey, string> = { open: "Open dispute", won: "Mark won", lost: "Mark lost", none: "Withdraw" };
export const moveText = (from: DisputeStatusKey, to: DisputeStatusKey) => (to === "open" && from !== "none" ? "Reopen" : MOVE_TEXT[to]);
const FLAG_TEXT: Record<VarianceFlagKey, string> = { unbilled: "Unbilled", match: "Match", over: "Over", under: "Under" };
export const flagText = (f: VarianceFlagKey) => FLAG_TEXT[f];
const DISPUTE_TEXT: Record<DisputeStatusKey, string> = { none: "No dispute", open: "Disputed", won: "Dispute won", lost: "Dispute lost" };
export const disputeText = (d: DisputeStatusKey) => DISPUTE_TEXT[d];

/** +$42.10 (+5.2%) / −$3.00 (−0.4%) / $0.00; bps null -> amount only. */
export function varianceText(cents: number, bps: number | null): string {
  const sign = cents > 0 ? "+" : cents < 0 ? "−" : "";
  const amount = `${sign}${money(Math.abs(cents))}`;
  if (bps === null || cents === 0) return amount;
  return `${amount} (${sign}${(Math.abs(bps) / 100).toFixed(1)}%)`;
}

/** Tolerance in words, from the API: "$5.00 or 2%, whichever is larger". */
export const toleranceText = (t: { cents: number; bps: number }) => `${money(t.cents)} or ${(t.bps / 100).toFixed(0)}%, whichever is larger`;

/* ---------------- AmFast (ops-entered carrier status) ---------------- */

/** Mirrors SHIPMENT_STATUSES minus draft (the API validates the same enum). */
export const AMFAST_STATUSES = ["booked", "dispatched", "picked_up", "in_transit", "out_for_delivery", "delivered", "exception", "cancelled"] as const;
export type AmFastStatus = (typeof AMFAST_STATUSES)[number];
export const SHIPMENT_STATUS_TEXT: Record<string, string> = {
  draft: "Draft",
  booked: "Booked",
  dispatched: "Dispatched",
  picked_up: "Picked up",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  exception: "Exception",
  cancelled: "Cancelled",
};
export const shipmentStatusText = (s: string): string => SHIPMENT_STATUS_TEXT[s] ?? s;

/** What the status form collects; all strings so inputs stay controlled. */
export type AmFastStatusDraft = {
  status: AmFastStatus;
  /** datetime-local value (local time). */
  at: string;
  description: string;
  city: string;
  region: string;
  exceptionCode: string;
  pro: string;
  pickupConfirmation: string;
  estimatedDelivery: string;
  signedBy: string;
};
export type AmFastStatusBody = {
  status: AmFastStatus;
  at: string;
  description: string;
  location?: { city: string; region: string };
  exceptionCode?: string;
  pro?: string;
  pickupConfirmation?: string;
  estimatedDelivery?: string;
  signedBy?: string;
};

const REF = /^[A-Za-z0-9-]{1,40}$/;
const EXCEPTION_CODE = /^[A-Z0-9_]{1,20}$/;

/** Field errors keyed by draft field; empty object means valid. Mirrors the API's zod schema. */
export function validateAmFastStatus(d: AmFastStatusDraft, now: number = Date.now()): Partial<Record<keyof AmFastStatusDraft, string>> {
  const e: Partial<Record<keyof AmFastStatusDraft, string>> = {};
  if (!d.at || Number.isNaN(new Date(d.at).getTime())) e.at = "Enter when this happened.";
  else if (new Date(d.at).getTime() > now + 5 * 60_000) e.at = "Can't be in the future.";
  const desc = d.description.trim();
  if (!desc) e.description = "Describe the update.";
  else if (desc.length > 500) e.description = "Keep it under 500 characters.";
  const city = d.city.trim();
  const region = d.region.trim();
  if (city || region) {
    if (!city || city.length > 80) e.city = "Enter the city.";
    if (!/^[A-Za-z]{2,3}$/.test(region)) e.region = "Use the 2-3 letter state or province code.";
  }
  if (d.exceptionCode.trim() && !EXCEPTION_CODE.test(d.exceptionCode.trim())) e.exceptionCode = "Capital letters, digits and underscores only.";
  if (d.status === "exception" && !d.exceptionCode.trim()) e.exceptionCode = "Exceptions need a code.";
  for (const k of ["pro", "pickupConfirmation"] as const) {
    if (d[k].trim() && !REF.test(d[k].trim())) e[k] = "Letters, digits and dashes only.";
  }
  if (d.estimatedDelivery && !/^\d{4}-\d{2}-\d{2}$/.test(d.estimatedDelivery)) e.estimatedDelivery = "Pick a date.";
  if (d.signedBy.trim().length > 80) e.signedBy = "Keep it under 80 characters.";
  if (d.status === "delivered" && !d.signedBy.trim()) e.signedBy = "Who signed for it?";
  return e;
}

/** Builds the POST body: trims, drops blanks, converts local time to ISO with offset. */
export function buildAmFastStatusBody(d: AmFastStatusDraft): AmFastStatusBody {
  const body: AmFastStatusBody = { status: d.status, at: new Date(d.at).toISOString(), description: d.description.trim() };
  const city = d.city.trim();
  const region = d.region.trim().toUpperCase();
  if (city && region) body.location = { city, region };
  const code = d.exceptionCode.trim();
  if (code) body.exceptionCode = code;
  if (d.pro.trim()) body.pro = d.pro.trim();
  if (d.pickupConfirmation.trim()) body.pickupConfirmation = d.pickupConfirmation.trim();
  if (d.estimatedDelivery) body.estimatedDelivery = d.estimatedDelivery;
  if (d.status === "delivered" && d.signedBy.trim()) body.signedBy = d.signedBy.trim();
  return body;
}

export const AMFAST_UPLOAD_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/tiff"] as const;
export const MAX_DOCUMENT_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Client-side check before a POD/BOL upload (the API re-checks type, magic bytes and size). */
export function checkAmFastUpload(file: { type: string; size: number } | null): string | null {
  if (!file) return "Choose a file.";
  if (!(AMFAST_UPLOAD_TYPES as readonly string[]).includes(file.type)) return "Use a PDF, PNG, JPEG or TIFF.";
  if (file.size === 0) return "That file is empty.";
  if (file.size > MAX_DOCUMENT_UPLOAD_BYTES) return "Files must be 25 MB or smaller.";
  return null;
}

/* ---------------- Shipment import (EagleLoad CSV) ---------------- */

export const EAGLELOAD_MAX_BYTES = 2_000_000;
export const EAGLELOAD_MAX_ROWS = 2000;

export function checkEagleLoadFile(file: { name: string; type: string; size: number }): string | null {
  const csvName = /\.csv$/i.test(file.name);
  const csvType = !file.type || /^(text\/csv|text\/plain|application\/vnd\.ms-excel)$/i.test(file.type);
  if (!csvName || !csvType) return "Choose a .csv file (export the loads from EagleLoad as CSV).";
  if (file.size === 0) return "That file is empty.";
  if (file.size > EAGLELOAD_MAX_BYTES) return "That file is over 2 MB. Split the export by date range.";
  return null;
}

/** Loads a dry run says would be written (valid rows minus already-imported duplicates). */
export const newLoadCount = (r: { valid: number; duplicates: readonly string[] }): number => Math.max(0, r.valid - r.duplicates.length);
